import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import HexGrid from './HexGrid';
import GameToast from "./Toast/GameToast";
import ReactionToast from './ReactionToast/ReactionToast';
import MatchPolicyToast from './MatchPolicyToast/MatchPolicyToast';
import RoundEndModal from './Modals/RoundEndModal/RoundEndModal';
import GameEndModal from './Modals/GameEndModal/GameEndModal';
import CityCaptureModal from './Modals/CityCaptureModal/CityCaptureModal';
import CombatResultsModal from './Modals/CombatResultsModal/CombatResultsModal';
import GoblinsTrapKillNotice from './GoblinsTrapKillNotice/GoblinsTrapKillNotice';
import InitiativeModal from './Modals/InitiativeModal/InitiativeModal';
import Header from './Header/Header';
import {
  loadState,
  setGameType,
  resetModalVisibility,
  setHideModalsExceptGameEnd,
  setPlayers,
  setPolicyMarket,
  startGame,
  setExtendedGameResults,
  setMatchIsRanked,
} from '../redux/actions';
import {getRandomInitiativeShift} from "../utils/gameState/getRandomInitiativeShift";
import {getRandomGroundBackground, resolveGroundBackground} from "../configs/groundBackgrounds";
import {resolveLocalMatchGroundBackground} from "../utils/localMatch/resolveLocalMatchGroundBackground";
import {getTutorialBotActionIntervalMs, getTutorialChapter} from "../configs/tutorial";
import {resetTutorialBotScript} from "../utils/tutorial/tutorialBotScript";
import {useTutorialDiceSession} from "../utils/tutorial/useTutorialDiceSession";
import {resetBotStepGuards} from "../utils/bots/executeBotStep";
import TutorialModalHost from "./Tutorial/TutorialModalHost";
import TutorialUiArrow from "./Tutorial/TutorialUiArrow/TutorialUiArrow";
import TutorialTasksPanel from "./Tutorial/TutorialTasksPanel/TutorialTasksPanel";
import MissionRunner from './MissionRunner/MissionRunner';
import MissionCompleteModal from './Mission/MissionCompleteModal/MissionCompleteModal';
import {getCampaignMission} from '../configs/campaigns';
import {missionStart} from '../redux/actions';
import MissionReplicaCharacter from './Mission/MissionReplica/MissionReplicaCharacter';
import {Footer} from "./Footer/Footer";
import {createDefaultSharedGameState} from "../utils/createDefaultSharedGameState";
import {applyFactionColorsToLocalGame} from "../utils/players/applyFactionColorsToLocalGame";
import {EMPTY_GAME_FIELD} from "../configs/fields";
import {FACTION_IDS_IMPLEMENTED} from "../configs/factions";
import UnitUpgradeModal from "./Modals/UnitUpgradeModal/UnitUpgradeModal";
import RepairBuildingModal from "./Modals/RepairBuildingModal/RepairBuildingModal";
import ConstructionActionsConfirmModal from "./Modals/ConstructionActionsConfirmModal/ConstructionActionsConfirmModal";
import WarStageUnitsConfirmModal from "./Modals/WarStageUnitsConfirmModal/WarStageUnitsConfirmModal";
import SupplyFarmsConfirmModal from "./Modals/SupplyFarmsConfirmModal/SupplyFarmsConfirmModal";
import SupplyRedistributeConfirmModal from './Modals/SupplyRedistributeConfirmModal/SupplyRedistributeConfirmModal';
import PlayersListSideMenu from "./Header/PlayersListSideMenu/PlayersListSideMenu";
import EffectsAutoPlayer from "./EffectsAutoPlayer/EffectsAutoPlayer";
import InGameMenu from "./Header/InGameMenu/InGameMenu";
import LoadingScreen from './LoadingScreen/LoadingScreen';
import SetupPlayersModal, {
  SETUP_PLAYER_MODAL_DURATION_PER_PLAYER,
  SETUP_PLAYER_MODAL_PLAYER_ANIMATION_DURATION_MS
} from './Modals/SetupPlayersModal/SetupPlayersModal';
import {REGISTRY_SINGLETON} from "../scenes/Registry";
import EntitiesAutoSelector from "./EntitiesAutoSelector/EntitiesAutoSelector";
import UnitAutoSelector from "./UnitAutoSelector/UnitAutoSelector";
import {GAME_TYPES} from "../constants/game";
import {
  dispatchReplayBootstrapActions,
  splitReplayBootstrapActions
} from "../utils/replay/splitReplayBootstrapActions";
import {FIELD_REDRAW_SIZE_THRESHOLD, DOCUMENT_SCROLL_LOCK_CLASS} from "../constants/visuals";
import {useFieldInsets} from "../hooks/visuals/useFieldInsets";
import {checkIfOrientationLockActive} from "../utils/device/checkIfOrientationLockActive";
import {checkIfViewportShrunkByKeyboard} from "../utils/dom/checkIfViewportShrunkByKeyboard";
import {checkIfTextEntryTarget} from "../utils/dom/checkIfTextEntryTarget";
import {PHASES} from "../constants/phases";
import {GAME_FIELD_READY_EVENT} from "../constants/sound";
import {
  selectGameResults,
  selectGameType,
  selectPhase,
  selectPlayers as selectGamePlayers
} from "../redux/reducers/sharedStateReducer/selectors";
import {
  selectFactionLoadouts,
  selectIsCityCaptureAnimationPlaying,
  selectIsRuinsIncomeAnimationPlaying,
  selectServerTimeDelta,
  selectUnitSkins
} from "../redux/reducers/privateStateReducer/selectors";
import {usePageAnalytics} from '../hooks/analytics/usePageAnalytics';
import {addNetworkMessageSubscriber} from '../utils/networkSocket';
import {useStaleGameStateWatchdog} from '../hooks/network/useStaleGameStateWatchdog';
import {useTurnInputLock} from '../hooks/network/useTurnInputLock';
import {addMidGameReconnectedHandler} from '../utils/connectionMonitor';
import {getActingPlayer} from '../utils/players/getActingPlayer';
import {SPECTATOR_ID} from '../constants/players';
import PolicyMarketSideMenu from "./Header/PolicyMarketSideMenu/PolicyMarketSideMenu";
import {SideMenuTabsProvider} from "./Header/SideMenuTabs/SideMenuTabs";
import {GameUiRootContext} from "./gameUi/GameUiPortal";
import {getNewPolicyMarket} from "../utils/policy/getNewPolicyMarket";
import './GameContent.css';
import '../styles/gameUi/tokens.css';
import '../styles/gameUi/panelButtons.css';
import {getGameUiPhase} from '../utils/gameUi/getGameUiPhase';
import {useCityCapturePanelsFade} from '../hooks/visuals/useCityCapturePanelsFade';
import {VisualTogglesAutoSwitch} from "./VisualToggles/VisualToggles";
import IsolationismModal from "./Modals/IsolationismModal/IsolationismModal";
import MatchToastStack from "./MatchToastStack/MatchToastStack";
import {PlayersListSideMenuProvider} from "../contexts/PlayersListSideMenuContext";
import {SideMenuStackProvider} from "../contexts/SideMenuStackContext";
import {PolicyMarketSideMenuProvider} from "../contexts/PolicyMarketSideMenuContext";
import {ReplayControlsProvider} from "../contexts/ReplayControlsContext";
import CampfireSparks from "./BackgroundEffects/CampfireSparks";
import BotRunner from "./BotRunner/BotRunner";
import OnlineBotRunner from "./OnlineBotRunner/OnlineBotRunner";
import BotReactionsSimulator from "./BotReactionsSimulator/BotReactionsSimulator";
import ReplayRunner from "./ReplayRunner/ReplayRunner";
import ReplayPlaybackPanel from "./ReplayPlaybackPanel/ReplayPlaybackPanel";
import TutorialReplayStatusPanel from "./Tutorial/TutorialReplayStatusPanel/TutorialReplayStatusPanel";
import {
  getLocalMatchReplayRecorder,
  getTutorialReplayRecorder
} from "../utils/tutorial/replay/tutorialReplayRecorderRegistry";
import {sendTutorialEvent} from "../analytics/gameAnalytics";
import {bootstrapTutorialChapter} from "../utils/tutorial/bootstrapTutorialChapter";
import {preloadBarbaPortrait} from "./Tutorial/BarbaReplicaModal/BarbaReplicaModal";
import {BOT_ACTION_INTERVAL_MS} from "../utils/bots/botConfig";
import {readHotSeatBotSpeed} from "../utils/bots/readHotSeatBotSpeed";
import {checkIfHumanPlayerPresent} from "../utils/localMatch/replay/localRunServerRules";

function GameContent(props) {
  const {
    field,
    roundCount,
    players,
    isSpectatorMode = false,
    isLocalTimerEnabled = false,
    // Подложка поля, выбранная игроком перед локальной партией (см.
    // HotSeatPlayersInitPage); null — «Случайная»
    chosenGroundBackground = null,
    // Разметка поля для сюжетной миссии — не партия, писать нечего
    isMissionEditorMode = false,
    setScreen,
    handleRankedGame,
    replayActions,
    replayFallbackResults = null,
    // Просмотр записи прохождения обучения (админ): паузы между действиями —
    // как у игрока, и панель с шагом главы
    isTutorialReplay = false,
    tutorialChapterId = null,
    onTutorialNextChapter,
    onTutorialOpenInventory,
    onTutorialExitToMenu,
    // «В битву» с поздравления последней главы: лобби сетевого матча
    onNewbieBattle,
    missionId = null,
    onMissionRestart,
    onMissionOpenInventory,
    onMissionExitToMenu,
  } = props;

  const dispatch = useDispatch();
  const gameType = useSelector(selectGameType);

  // Кости обучения глушатся при выходе из партии: вне обучения генератор
  // должен быть честным, даже если очередь не была допотреблена
  const startTutorialDiceSession = useTutorialDiceSession(tutorialChapterId);
  const factionLoadouts = useSelector(selectFactionLoadouts);
  const unitSkins = useSelector(selectUnitSkins);
  const auth = useSelector((state) => state.private.auth);
  const isAdminOrSuperAdmin = auth.isAdmin || auth.isSuperAdmin;
  // Запись локального матча (см. localMatchReplayMiddleware) ведется только у
  // обычной партии авторизованного игрока: горячее кресло и игра против
  // ботов. Обучение пишется своим рекордером, у миссий кампании, редактора
  // миссий, режима зрителя «боты против ботов» и гостя записи нет
  const isLocalMatchRecorded = !isMissionEditorMode && !isSpectatorMode
    && !tutorialChapterId && !missionId
    && Boolean(auth.id) && !auth.isGuest;
  const serverTimeDelta = useSelector(selectServerTimeDelta);

  // Пул для нерезолвленной «случайной» фракции в локальной игре: только
  // доступные пользователю фракции (как в сетевой); фолбэк на все
  // реализованные — при отсутствии данных о владении (гость/оффлайн)
  const randomFactionIdsPool = useMemo(() => {
    const unlockedIds = new Set(
      (auth.availableFactions || []).filter((faction) => faction.isAvailable).map((faction) => faction.id)
    );
    const availableIds = FACTION_IDS_IMPLEMENTED.filter((id) => unlockedIds.has(id));
    return availableIds.length > 0 ? availableIds : FACTION_IDS_IMPLEMENTED;
  }, [auth.availableFactions]);

  const isDefaultStateLoadInited = useRef(false);
  const isReplayBootstrapInited = useRef(false);

  // Смонтирована ли партия прямо сейчас — на все время жизни компонента, а не
  // одного эффекта: поле главы обучения грузится асинхронно, и игрок может уйти
  // в меню раньше, чем оно пришло. Тогда ни глава, ни запись прохождения не
  // должны стартовать поверх меню. Запись ушедшей партии закрывается здесь же:
  // не каждый выход с экрана игры проходит через RESET_STATE
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      getTutorialReplayRecorder()?.endRun();
      getLocalMatchReplayRecorder()?.endRun();
    };
  }, []);

  const [isInGameMenuOpen, setIsInGameMenuOpen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isHexGridReady, setIsHexGridReady] = useState(false);
  const [isSetupDelayComplete, setIsSetupDelayComplete] = useState(false);
  const [isSetupDelaySkipped, setIsSetupDelaySkipped] = useState(false);
  const [isSetupModalVisible, setIsSetupModalVisible] = useState(true);
  const [isRedrawing, setIsRedrawing] = useState(false);
  const [redrawReason, setRedrawReason] = useState('resize');

  const gamePlayers = useSelector(selectGamePlayers);
  // HexGrid не монтируем, пока игроки (factionId/hasUnitSkin) не определены
  // в сторе — GameScene.preload() читает players синхронно при создании
  // сцены и должен видеть уже финальные данные, а не пустой/устаревший стейт
  const isPlayersReady = gamePlayers && gamePlayers.length > 0;
  // Режим зрителя для LOCAL — только явная галочка на HotSeatPlayersInitPage
  // (проп из App, отфильтрован по gameType, чтобы не протекал в следующую
  // партию другого типа). Партия «все боты» без галочки — обычный UI.
  // Просмотр записи матча (REPLAY) — всегда режим зрителя, App.js всегда
  // передает isSpectatorMode=true при запуске воспроизведения
  const isSpectatorHotSeatGame = isSpectatorMode && (gameType === GAME_TYPES.LOCAL || gameType === GAME_TYPES.REPLAY);
  // Режим зрителя для ONLINE — не флаг с чекбокса, а факт: сервер не выдал
  // этому пользователю место игрока (currentPlayer здесь не нужен, он
  // используется только в LOCAL-ветке getActingPlayer)
  const isNetworkSpectator = gameType === GAME_TYPES.ONLINE
    && getActingPlayer(gameType, auth.id, gamePlayers, null).id === SPECTATOR_ID;
  const isSpectatorUIMode = isSpectatorHotSeatGame || isNetworkSpectator;
  const phase = useSelector(selectPhase);
  const gameResults = useSelector(selectGameResults);
  // Во время облета захваченных городов интерфейс — цвета войны
  const isCityCaptureAnimationPlaying = useSelector(selectIsCityCaptureAnimationPlaying);
  const isRuinsIncomeAnimationPlaying = useSelector(selectIsRuinsIncomeAnimationPlaying);
  const gameUiPhase = getGameUiPhase(phase, gameResults, isCityCaptureAnimationPlaying, isRuinsIncomeAnimationPlaying);
  const setupDelayDeadlineRef = useRef(null);
  const replayGameplayActionsRef = useRef(null);
  const replayBootstrapActionsRef = useRef(null);

  usePageAnalytics('GameContent');

  const suppressDefaultRef = useRef(false);
  const [hideGame, setHideGame] = useState(false);
  // Размеры окна на момент последней РЕАЛЬНОЙ отрисовки поля — база порога
  // ниже (обновляется в handleGameReady). Именно база от последней отрисовки,
  // а не от предыдущего resize: иначе цепочка изменений по чуть-чуть увела бы
  // поле в любой размер, ни разу не пройдя порог
  const lastRenderedSizeRef = useRef({width: window.innerWidth, height: window.innerHeight});
  // Вход/выход из полноэкранного режима — перерисовка обязательна независимо
  // от порога: полный экран часто добавляет/убирает меньше 10% высоты, и без
  // принудительной перерисовки поле осталось бы растянутым (канва тянется CSS
  // на 100% контейнера, см. FIELD_REDRAW_SIZE_THRESHOLD)
  const forceRedrawRef = useRef(false);
  useEffect(() => {
    let timeoutId;
    // Последнюю проверку пропустили из-за клавиатуры — ее надо повторить,
    // когда поле ввода потеряет фокус (см. handleTextEntryFocusOut)
    let isRecheckOwedAfterKeyboard = false;

    const redrawIfSizeChangedEnough = () => {
      // Пока висит оверлей «поверните устройство», поле перекрыто целиком —
      // перерисовывать под ним нечего. Обратный поворот сам даст resize, и
      // тогда сработает проверка порога ниже (размер обычно вернется к
      // базовому — перерисовки не будет вовсе). Флаг принудительной
      // перерисовки при этом НЕ сбрасываем: если полноэкранный режим
      // переключили под оверлеем, перерисовать поле все равно придется
      if (checkIfOrientationLockActive()) {
        return;
      }

      const isForcedRedraw = forceRedrawRef.current;
      forceRedrawRef.current = false;
      isRecheckOwedAfterKeyboard = false;

      // Экранная клавиатура (чат матча) в части браузеров сжимает окно почти
      // вдвое. Подгонять поле под полоску над клавиатурой, а после ее
      // закрытия — обратно, незачем: кадр прыгал бы на каждом сообщении.
      // Базу не трогаем: клавиатура закроется, размер вернется к ней. Если же
      // окно сжала не клавиатура, проверку повторит handleTextEntryFocusOut
      if (!isForcedRedraw && checkIfViewportShrunkByKeyboard(lastRenderedSizeRef.current)) {
        isRecheckOwedAfterKeyboard = true;
        return;
      }

      // Поле матча подстраивает буфер под окно на месте — без заставки и без
      // пересоздания сцены, на любое изменение размера (см.
      // GameScene.resizeToWindow). Пересоздание ниже остается запасным путем:
      // сцена еще грузится или это не поле матча
      if (REGISTRY_SINGLETON.getGameScene()?.resizeToWindow?.()) {
        lastRenderedSizeRef.current = {width: window.innerWidth, height: window.innerHeight};
        return;
      }

      if (!isForcedRedraw) {
        const {width: baseWidth, height: baseHeight} = lastRenderedSizeRef.current;
        const widthDelta = Math.abs(window.innerWidth - baseWidth) / baseWidth;
        const heightDelta = Math.abs(window.innerHeight - baseHeight) / baseHeight;
        if (widthDelta < FIELD_REDRAW_SIZE_THRESHOLD && heightDelta < FIELD_REDRAW_SIZE_THRESHOLD) {
          return;
        }
      }

      suppressDefaultRef.current = true;
      setRedrawReason('resize');
      setIsRedrawing(true);
      setHideGame(true);
      setTimeout(() => {
        setHideGame(false);
        setTimeout(() => {
          suppressDefaultRef.current = false;
        }, 0);
      }, 50);
    };

    // Подстройка поля матча на месте дешевая, и ждать дебаунса ей незачем: до
    // него буфер канвы остается старого размера, а CSS растягивает его на
    // новое окно — поле на треть секунды сплющивалось бы. Поэтому буфер
    // подгоняется сразу после текущей пачки событий (таймер 0, а не кадр
    // анимации: тот в фоновой вкладке не приходит вовсе); клавиатуру и оверлей
    // поворота проверяем так же, как в основной проверке
    let resizeTimeoutId = null;
    const resizeInPlaceSoon = () => {
      if (resizeTimeoutId !== null) {
        return;
      }
      resizeTimeoutId = setTimeout(() => {
        resizeTimeoutId = null;
        if (checkIfOrientationLockActive() || checkIfViewportShrunkByKeyboard(lastRenderedSizeRef.current)) {
          return;
        }
        if (REGISTRY_SINGLETON.getGameScene()?.resizeToWindow?.()) {
          lastRenderedSizeRef.current = {width: window.innerWidth, height: window.innerHeight};
        }
      }, 0);
    };

    const scheduleRedrawCheck = () => {
      resizeInPlaceSoon();
      clearTimeout(timeoutId);
      // Тот же дебаунс, что и у resize: смена полноэкранного режима порождает
      // собственный шторм resize-событий, и к моменту проверки размеры окна
      // уже устоялись (на самом событии fullscreenchange они еще старые)
      timeoutId = setTimeout(redrawIfSizeChangedEnough, 300);
    };

    // Слушаем именно событие, а не клик по кнопке: из полного экрана выходят
    // еще и по Esc, системным жестом и из App.js — кнопка все пути не покрывает
    const handleFullscreenChange = () => {
      forceRedrawRef.current = true;
      scheduleRedrawCheck();
    };

    const FULLSCREEN_CHANGE_EVENTS = [
      'fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange',
    ];

    // Клавиатуру от разделения экрана по размерам окна не отличить: и то, и
    // другое делает его ниже при прежней ширине. Поэтому проверку, которую
    // пропустили из-за фокуса в поле ввода, повторяем, когда поле фокус
    // теряет: события resize после этого может уже не быть, и поле осталось
    // бы сплющенным. Задержка заведомо длиннее скрытия клавиатуры — иначе
    // проверка застала бы окно еще сжатым и пересобрала поле зря; resize от
    // закрывшейся клавиатуры сам перезапустит таймер обычным дебаунсом.
    // Без пропущенной проверки повторять нечего: лишний запуск сравнил бы
    // окно со старой базой и перезапустил пересборку, которая уже идет
    const handleTextEntryFocusOut = (event) => {
      if (!isRecheckOwedAfterKeyboard || !checkIfTextEntryTarget(event.target)) {
        return;
      }
      clearTimeout(timeoutId);
      timeoutId = setTimeout(redrawIfSizeChangedEnough, 1000);
    };

    window.addEventListener('resize', scheduleRedrawCheck);
    document.addEventListener('focusout', handleTextEntryFocusOut);
    FULLSCREEN_CHANGE_EVENTS.forEach((eventName) =>
      document.addEventListener(eventName, handleFullscreenChange));
    return () => {
      window.removeEventListener('resize', scheduleRedrawCheck);
      document.removeEventListener('focusout', handleTextEntryFocusOut);
      FULLSCREEN_CHANGE_EVENTS.forEach((eventName) =>
        document.removeEventListener(eventName, handleFullscreenChange));
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeoutId);
    };
  }, []);

  // Экран партии целиком собран из fixed-панелей (шапка, поле, футер, боковые
  // меню со своим внутренним скроллом), прокручивать в нем нечего — а вот
  // document-скролл ломает попадание по клеткам: Phaser запоминает границы
  // канвы вместе со скроллом, а хит-тестит по координатам с текущим (см.
  // syncCanvasBounds в GameScene). Запираем скролл на время партии, чтобы
  // смещения не возникало вовсе; глобально так делать нельзя — меню, магазин
  // и правила скроллятся законно
  useEffect(() => {
    // Экран мог открыться с уже прокрученной страницы — иначе заперли бы
    // ненулевое смещение
    window.scrollTo(0, 0);
    document.documentElement.classList.add(DOCUMENT_SCROLL_LOCK_CLASS);
    return () => document.documentElement.classList.remove(DOCUMENT_SCROLL_LOCK_CLASS);
  }, []);

  // Сторож зависшего рендера: цикл отрисовки Phaser способен умереть насовсем
  // (исключение внутри кадра не дает заказать следующий), и поле застывает на
  // последнем кадре. Лечение то же, что игроки нащупывали руками через
  // полноэкранный режим, — перемонтировать поле
  const isRedrawingRef = useRef(false);
  isRedrawingRef.current = isRedrawing;
  const handleRenderStalled = useCallback(() => {
    // Перерисовка уже идет — второй раз поле не дергаем
    if (isRedrawingRef.current) {
      return;
    }
    setRedrawReason('stalled');
    setIsRedrawing(true);
    setHideGame(true);
    setTimeout(() => {
      setHideGame(false);
    }, 50);
  }, []);

  // Тот же прием, что и при ресайзе окна: после восстановления сокета
  // посреди матча (см. LobbyPage) свежий LOAD_STATE еще не отрисован —
  // перемонтируем HexGrid и прикрываем поле оверлеем до onGameReady
  useEffect(() => addMidGameReconnectedHandler(() => {
    setRedrawReason('reconnect');
    setIsRedrawing(true);
    setHideGame(true);
    setTimeout(() => {
      setHideGame(false);
    }, 50);
  }), []);

  // Зрителю сетевого матча всплывающие окна по ходу партии не нужны: решений
  // он не принимает, а модалки чужих ходов перекрывали бы поле. Ставим
  // значение ОДИН раз — как только сервер подтвердил состав игроков (до его
  // загрузки getActingPlayer вернул бы SPECTATOR_ID и обычному игроку тоже);
  // дальше пользователь волен переключить галочку в InGameMenu.
  // Обычному игроку выставляем false явно: флаг живет вне партии, и
  // просмотренная перед матчем чужая игра или запись оставила бы его
  // включенным (см. App.js — для локальных партий и реплеев то же самое)
  //
  // Повторно — только когда роль меняется посреди матча: админ-зритель
  // перехватил управление ботом и стал игроком (окна нужны) или вернул
  // управление и снова стал зрителем (окна прячутся). Галочку пользователя в
  // InGameMenu, пока роль прежняя, не трогаем
  const spectatorModalsAppliedRoleRef = useRef(null);
  useEffect(() => {
    if (gameType !== GAME_TYPES.ONLINE || !isPlayersReady) return;
    if (spectatorModalsAppliedRoleRef.current === isNetworkSpectator) return;
    // Зритель стал игроком посреди матча (перехватил бота): пока окна были
    // скрыты, их флаги копились (см. modalsMiddleware) — без сброса все
    // устаревшие окна раундов и боев открылись бы разом
    if (spectatorModalsAppliedRoleRef.current === true && !isNetworkSpectator) {
      dispatch(resetModalVisibility());
    }
    spectatorModalsAppliedRoleRef.current = isNetworkSpectator;
    dispatch(setHideModalsExceptGameEnd(isNetworkSpectator));
  }, [dispatch, gameType, isPlayersReady, isNetworkSpectator]);

  // Состояние матча могло протухнуть незаметно (экшены не доехали, а сокет жив) —
  // сторож попросит у сервера полное состояние (см. useStaleGameStateWatchdog)
  useStaleGameStateWatchdog();
  // Отступы свободной области поля под шапкой, футером и панелями — по ним
  // камера ставит кадры (см. GameScene.centerOnFreeArea)
  useFieldInsets();
  // Время хода вышло — до серверного закрытия действия игрока не принимаются
  // (см. useTurnInputLock)
  useTurnInputLock();

  // Подписка на трафик матча (см. networkSocket): к объекту сокета не привязана,
  // поэтому переустанавливать ее после переподключения не нужно
  useEffect(() => addNetworkMessageSubscriber((msg) => {
    if (msg.type === 'GAME_ENDED') {
      window.dispatchEvent(new CustomEvent('gameendedbyhost'));
    }
  }), []);

  const handleLoadingProgress = useCallback((value) => {
    setLoadingProgress(Math.round(value * 100));
  }, []);

  const handleGameReady = useCallback(() => {
    setIsHexGridReady(true);
    setIsRedrawing(false);
    // Ассеты поля загружены — теперь и музыке партии можно грузить свой трек
    window.dispatchEvent(new CustomEvent(GAME_FIELD_READY_EVENT));
    // Поле отрисовано под текущий размер окна — он и становится новой базой
    // для порога перерисовки (см. lastRenderedSizeRef выше)
    lastRenderedSizeRef.current = {width: window.innerWidth, height: window.innerHeight};
  }, []);

  // В сетевой игре и в записи состояние своего матча лежит в сторе уже к
  // монтированию (сервер шлет LOAD_STATE до перехода на экран). В локальной,
  // обучении и миссии оно приходит позже — а до тех пор там висит партия
  // витрины главного меню, которая тоже стоит в фазе SETUP. Представление
  // игроков по такому состоянию мелькало и гасло через доли секунды, когда
  // приходил свой LOAD_STATE
  const [isForeignSetupState, setIsForeignSetupState] = useState(() => (
    gameType !== GAME_TYPES.ONLINE
    && gameType !== GAME_TYPES.REPLAY
    && phase === PHASES.SETUP
  ));
  // Признак снимается, как только приходит состав СВОЕГО матча. Именно состав,
  // а не фаза: свои LOAD_STATE и START_GAME прилетают одним батчем, и чужая
  // фаза SETUP переходит в свою незаметно для рендера. Любая из веток
  // бутстрапа (локальная партия, обучение, миссия) кладет новый массив
  // игроков — этого достаточно
  const foreignPlayersRef = useRef(gamePlayers);
  useEffect(() => {
    if (isForeignSetupState && gamePlayers !== foreignPlayersRef.current) {
      setIsForeignSetupState(false);
    }
  }, [isForeignSetupState, gamePlayers]);

  useEffect(() => {
    if (phase !== PHASES.SETUP) return;

    setIsSetupModalVisible(true);
    setIsSetupDelaySkipped(false);
    setIsSetupDelayComplete(false);
    setupDelayDeadlineRef.current = null;
  }, [phase]);

  // Представление игроков на экране: в фазе расстановки и пока его не
  // досмотрели/не пропустили. Пока в сторе чужая партия, плашка стоит пустой
  // (isForeignSetupState) — иначе на ее месте мелькал бы экран загрузки, а
  // строки чужого состава показывать нельзя
  const isSetupRevealVisible = phase === PHASES.SETUP
    && isSetupModalVisible
    && !isSetupDelayComplete
    && !isSetupDelaySkipped;

  // Состав уже свой — можно отсчитывать длительность показа
  const isSetupRevealPlaying = isSetupRevealVisible && !isForeignSetupState;

  // Представление игроков держится по СВОЕМУ времени, а не по тому, сколько
  // прожил конкретный таймер: эффект перезапускается на каждом изменении
  // состава (в сетевом матче игроки меняются по ходу расстановки), и его
  // уборка гасит уже поставленный таймер. Раньше он после этого не взводился
  // заново — представление обрывалось не по времени, а по первому же событию,
  // менявшему фазу. Поэтому храним АБСОЛЮТНЫЙ дедлайн и каждый раз ставим
  // таймер на остаток
  useEffect(() => {
    if (!isSetupRevealPlaying) return;

    if (setupDelayDeadlineRef.current === null) {
      const playerCount = gamePlayers?.length || 0;
      if (playerCount === 0) return;

      // Отсчет идет от появления представления на экране, а не от готовности
      // поля: строки начинают вылетать сразу, и привязка к другому моменту
      // укорачивала показ на время загрузки
      setupDelayDeadlineRef.current = Date.now()
        + playerCount * SETUP_PLAYER_MODAL_DURATION_PER_PLAYER
        + SETUP_PLAYER_MODAL_PLAYER_ANIMATION_DURATION_MS;
    }

    const remainingMs = Math.max(0, setupDelayDeadlineRef.current - Date.now());
    const timer = setTimeout(() => setIsSetupDelayComplete(true), remainingMs);
    return () => clearTimeout(timer);
  }, [isSetupRevealPlaying, gamePlayers]);

  // Показывать нечего — партию не задерживаем: клиент подключился уже после
  // расстановки, либо состав так и не пришел
  useEffect(() => {
    if (!isHexGridReady || isSetupDelayComplete || isSetupDelaySkipped) return;
    if (phase !== PHASES.SETUP || (!isSetupRevealPlaying && setupDelayDeadlineRef.current === null)) {
      setIsSetupDelayComplete(true);
    }
  }, [isHexGridReady, phase, isSetupRevealPlaying, isSetupDelayComplete, isSetupDelaySkipped]);

  const isGameReady = isHexGridReady && (phase !== PHASES.SETUP || isSetupDelayComplete || isSetupDelaySkipped);

  const handleSkipSetupDelay = () => {
    setIsSetupDelaySkipped(true);
    setIsSetupModalVisible(false);
  };

  const handleOpenInGameMenu = () => {
    REGISTRY_SINGLETON.getSoundManager().playMenuButtonClick();
    setIsInGameMenuOpen(true);
  };
  const handleCloseInGameMenu = () => {
    setIsInGameMenuOpen(false);
  };

  // Загружаем состояние игры по умолчанию при первом рендере, если это игра в режиме hotSeat
  // (ONLINE получает состояние через LOAD_STATE от сервера, REPLAY — из
  // собственного bootstrap-эффекта ниже, из записанных экшенов матча)
  useEffect(() => {
    if (isDefaultStateLoadInited.current === true || gameType === GAME_TYPES.ONLINE || gameType === GAME_TYPES.REPLAY || suppressDefaultRef.current) {
      isDefaultStateLoadInited.current = true;
      return;
    }

    isDefaultStateLoadInited.current = true;

    // Партия, которую не пишем (сюжетная миссия, редактор миссий, режим
    // зрителя, гость): прохождение предыдущей локальной партии этой вкладки
    // надо забыть — иначе итоги уехали бы с ее runId и закрыли бы чужую
    // запись, а свой матч в историю не попал бы (см. GameEndModal)
    if (!isLocalMatchRecorded) {
      getLocalMatchReplayRecorder()?.forgetRun();
    }

    // Режим обучения: собственный бутстрап — фиксированные цвета (совпадают с
    // полем tutorial*.json, перекраска под фракции не нужна), нулевой сдвиг
    // инициативы (игрок ходит первым), скриптованный бот и старт сценария
    // Миссия сюжетной кампании: поле и состав игроков берём из конфига
    // миссии (снимок редактора), а ходы противников будет воспроизводить
    // лента — см. missionBotScript
    if (missionId) {
      const mission = getCampaignMission(missionId);
      if (!mission) {
        console.error(`[Mission] Unknown campaign mission: ${missionId}`);
        return;
      }

      resetBotStepGuards();
      dispatch(setGameType(GAME_TYPES.LOCAL));
      dispatch(loadState({
        hexes: mission.field?.hexes || {},
        units: mission.field?.units || [],
        specialBuildings: mission.field?.specialBuildings || {},
        gridConfig: mission.field?.gridConfig,
      }, mission.finalRound ?? null));
      // Имя живого игрока — из профиля, как в обучении
      dispatch(setPlayers((mission.players || []).map((player) => (
        player.color === mission.playerColor
          ? {...player, name: auth.name || player.name, isBot: false}
          : {...player, isBot: true}
      ))));
      dispatch(setPolicyMarket(mission.policyMarket || getNewPolicyMarket()));
      // Нулевой сдвиг инициативы: порядок ходов должен совпасть с записью
      dispatch(startGame(
        Date.now() + serverTimeDelta, false, false, 0, false, getRandomGroundBackground()
      ));
      dispatch(missionStart(mission.id, mission.script || [], mission.playerColor));
      return;
    }

    if (tutorialChapterId) {
      const chapter = getTutorialChapter(tutorialChapterId);
      if (!chapter) {
        console.error(`[Tutorial] Unknown chapter: ${tutorialChapterId}`);
        return;
      }
      preloadBarbaPortrait(chapter);
      fetch(chapter.fieldPath)
        .then((response) => {
          if (!response.ok) throw new Error(`Tutorial field not found: ${chapter.fieldPath}`);
          return response.json();
        })
        .then((data) => {
          if (!isMountedRef.current) {
            return;
          }
          resetBotStepGuards();
          resetTutorialBotScript();
          startTutorialDiceSession();
          bootstrapTutorialChapter({
            dispatch,
            chapter,
            fieldData: data,
            recorder: getTutorialReplayRecorder(),
            sendEvent: sendTutorialEvent,
            isMounted: () => isMountedRef.current,
            playerName: auth.name,
            completedChapterIds: auth.completedTutorialChapters,
            timestamp: Date.now() + serverTimeDelta,
          });
        })
        .catch((error) => {
          console.error('[Tutorial] Failed to bootstrap chapter:', error);
        });
      return;
    }

    // Фаза FIELD_PREPARATION (редактирование поля) в локальной игре доступна,
    // только если ее запустил админ/супер-админ. Остальным сразу же (после
    // загрузки players/policyMarket) программно "нажимаем" ту же кнопку
    // старта матча, что и CompletePhaseButton в этой фазе (dispatch startGame) —
    // именно она проставляет isStarted/turnOrder/currentPlayerIndex/доходы,
    // простого выставления phase=SETUP в LOAD_STATE для этого недостаточно.
    // Режим зрителя пропускает эту фазу даже для админа — редактировать поле
    // перед матчем ботов друг с другом незачем
    // Подложка поля выбирается ОДИН раз на партию и кладется в загружаемое
    // состояние — до того, как смонтируется поле: сцена читает ее в preload
    // (см. GameScene) и больше не перечитывает. Если разыгрывать ее в
    // START_GAME, у партии админа она разъедется с показанной: там матч
    // начинается позже, из фазы редактирования поля, и игрок все это время
    // смотрит на одну подложку, а в запись уходит другая.
    // Подложку мог назвать сам игрок на странице настройки партии; не назвал
    // (или назвал снятую с раздачи) — она случайная
    const localGroundBackground = resolveGroundBackground(chosenGroundBackground);

    const maybeAutoStartLocalGame = (fieldHexes, groundBackground) => {
      if (isAdminOrSuperAdmin && !isSpectatorMode) return;
      const flagsOnField = Object.values(fieldHexes).filter(token =>
        typeof token === 'string' && token.startsWith('flag_')
      ).length;
      if (flagsOnField === 0) return;
      // Подложку локальная партия выбирает сама (игрок или случай): сервера,
      // который решил бы за всех, здесь нет (см. groundBackground в performStartGame)
      dispatch(startGame(
        Date.now() + serverTimeDelta, isLocalTimerEnabled, false,
        getRandomInitiativeShift(players.length), false, groundBackground
      ));
    };

    // Запись локального матча начинается ДО первого экшена партии: экшены
    // поля, состава и старта должны попасть в нее целиком (иначе запись
    // нечем проигрывать). Сам документ матча создается на сервере уже после —
    // первой же отправкой (см. createTutorialReplayRecorder)
    const beginLocalMatchRun = (gamePlayers) => {
      if (!isLocalMatchRecorded) {
        return;
      }
      const runPlayers = (gamePlayers || []).map((player) => ({
        id: player.id,
        name: player.name || '',
        color: player.color || '',
        factionId: player.factionId || '',
        selectedSkin: player.selectedSkin || `${player.factionId || ''}_default`,
        isBot: Boolean(player.isBot),
        // Помечен ли бот значком и какой у него бонус: по ним в истории и на
        // странице матча рисуется его значок (см. getBotMarkIconPath)
        isLocalBot: Boolean(player.isLocalBot),
        bonus: player.bonus || '',
        // Числа бонуса, зафиксированные на старте (см. prepareLocalMatchPlayers),
        // — для подсказки значка и у незаконченной партии
        ...(player.bonusConfig ? {bonusConfig: player.bonusConfig} : {}),
      }));
      getLocalMatchReplayRecorder()?.beginRun({
        finalRound: roundCount ?? null,
        players: runPlayers,
      }, {
        // Партия, в которой игрок никого не оставил человеком, пишется только
        // фактом старта и итогов: сам матч в истории виден (в том числе
        // брошенный), а действий в записи нет — разбирать в ней нечего.
        // Признак — по составу партии, а не по режиму зрителя: тот включается
        // отдельной кнопкой и записи не заводит вовсе
        recordActions: checkIfHumanPlayerPresent(runPlayers),
      });
    };

    const loadDefaultSeaField = () => {
      const defaultState = {
        ...createDefaultSharedGameState(),
        timerEnabled: isLocalTimerEnabled,
        groundBackground: localGroundBackground,
      };
      dispatch(loadState(defaultState, roundCount));
      return defaultState;
    }

    const updateLocalGameState = (gamePlayers = players) => {
      dispatch(setPlayers(gamePlayers));
      dispatch(setPolicyMarket(getNewPolicyMarket()));
    };

    if (field && field.id !== EMPTY_GAME_FIELD.id) {
      fetch(field.path)
        .then(response => {
          if (!response.ok) {
            // Файл не найден - создаём состояние по умолчанию (все гексы - море)
            throw new Error('Save file not found, generating default sea grid...');
          }
          return response.json();
        })
        .then(data => {
          // Как на сервере при старте онлайн-игры: цвета игроков из конфигурации
          // поля заменяются на приоритетные цвета их фракций (и в ячейках поля).
          // Режим зрителя для админа — витрина всех фракционных скинов отрядов,
          // независимо от того, что реально куплено у самого админа
          const {
            coloredPlayers,
            coloredFieldData
          } = applyFactionColorsToLocalGame(
            players, data, factionLoadouts, unitSkins, isSpectatorMode && isAdminOrSuperAdmin,
            randomFactionIdsPool
          );
          beginLocalMatchRun(coloredPlayers);
          // Выбор игрока важнее своей подложки поля, а та — важнее случайной
          const groundBackground = resolveLocalMatchGroundBackground({
            chosenGroundBackground,
            fieldGroundBackground: coloredFieldData.groundBackground,
            partyGroundBackground: localGroundBackground,
          });
          dispatch(loadState(
            {...coloredFieldData, timerEnabled: isLocalTimerEnabled, groundBackground},
            roundCount
          ));
          updateLocalGameState(coloredPlayers);
          maybeAutoStartLocalGame(coloredFieldData.hexes, groundBackground);
        })
        .catch(error => {
          console.error('Error loading field:', error);
          // При ошибке также создаём состояние по умолчанию
          beginLocalMatchRun(players);
          const defaultState = loadDefaultSeaField();
          updateLocalGameState();
          maybeAutoStartLocalGame(defaultState.hexes, defaultState.groundBackground);
        });
    } else {
      beginLocalMatchRun(players);
      const defaultState = loadDefaultSeaField();
      updateLocalGameState();
      maybeAutoStartLocalGame(defaultState.hexes, defaultState.groundBackground);
    }
  }, [dispatch, isDefaultStateLoadInited, field, roundCount, players, gameType, suppressDefaultRef, factionLoadouts, unitSkins, isAdminOrSuperAdmin, serverTimeDelta, isSpectatorMode, isLocalTimerEnabled, chosenGroundBackground, randomFactionIdsPool, tutorialChapterId, missionId, auth.name, auth.completedTutorialChapters, startTutorialDiceSession, isLocalMatchRecorded]);

  // Bootstrap записи матча (REPLAY): начало записи — те же 5 экшенов
  // инициализации, что сервер диспатчит в performStartGame (SET_GAME_TYPE,
  // LOAD_STATE, SET_PLAYERS, SET_POLICY_MARKET, START_GAME) — их применяем
  // сразу и синхронно, как и ONLINE получает их мгновенно через LOAD_STATE,
  // иначе HexGrid не сможет смонтироваться. SET_GAME_TYPE из записи не
  // диспатчим — там сохранен ONLINE, а тип игры уже выставлен в REPLAY
  // снаружи (App.js) до перехода на этот экран. Все действия после этого
  // префикса — реальный игровой процесс, воспроизводится позже ReplayRunner
  // с паузами (см. isGameReady ниже), а не сразу
  useEffect(() => {
    if (isReplayBootstrapInited.current) return;
    if (gameType !== GAME_TYPES.REPLAY || !replayActions || replayActions.length === 0) return;

    isReplayBootstrapInited.current = true;

    const {bootstrapActions, gameplayActions} = splitReplayBootstrapActions(replayActions);
    dispatchReplayBootstrapActions(dispatch, bootstrapActions);

    // Награды СТАРЫХ записей (в них самих их нет) — применяем сразу после
    // служебного префикса: LOAD_STATE из него сбросил бы их, а игровые
    // действия к ним не притрагиваются. Модалка итогов откроется в конце и
    // возьмет уже готовые данные — без мигания «сначала без наград»
    if (replayFallbackResults) {
      if (replayFallbackResults.extendedResults) {
        dispatch(setExtendedGameResults(replayFallbackResults.extendedResults));
      }
      if (replayFallbackResults.isRanked) {
        dispatch(setMatchIsRanked(true));
      }
    }

    replayBootstrapActionsRef.current = bootstrapActions;
    replayGameplayActionsRef.current = gameplayActions;
  }, [dispatch, gameType, replayActions, replayFallbackResults]);

  // Скорость хода ботов настраивается только на HotSeatPlayersInitPage (localStorage),
  // читаем один раз при старте игры — сама страница к этому моменту уже размонтирована
  // Корень экрана матча — цель порталов модалок матча (см. GameUiPortal)
  const [gameUiRoot, setGameUiRoot] = useState(null);
  // Открытые к началу облета захваченных городов панели на время облета гаснут
  useCityCapturePanelsFade(isCityCaptureAnimationPlaying || isRuinsIncomeAnimationPlaying, gameUiRoot);

  const hotSeatBotActionIntervalMs = useMemo(
    () => Math.round(BOT_ACTION_INTERVAL_MS / readHotSeatBotSpeed()),
    []
  );

  return (
    <SideMenuStackProvider>
      <PlayersListSideMenuProvider>
      <PolicyMarketSideMenuProvider>
        <ReplayControlsProvider>
          {/* game-ui — скоуп дизайн-системы матча: токены из styles/gameUi/tokens.css
          действуют только внутри этого корня, меню их не видит. data-gui-phase
          выбирает цвет фазы для шапки и полосы этапов */}
          <div className="App game-ui" data-gui-phase={gameUiPhase} ref={setGameUiRoot}>
            <GameUiRootContext.Provider value={gameUiRoot}>
            {/* Вне ветки isGameReady: делегирование работы бота не должно теряться,
            пока клиент еще на экране загрузки (сообщение сервера одноразовое) */}
            {gameType === GAME_TYPES.ONLINE && <OnlineBotRunner/>}
            {/* Реакции ботов симулирует каждый клиент сам — гостю-зрителю это не
            нужно: иначе число реакций у живых игроков росло бы с каждым гостем */}
            {gameType === GAME_TYPES.ONLINE && !auth.isGuest && <BotReactionsSimulator/>}
            {isGameReady ?
              <>
                <EffectsAutoPlayer/>
                <EntitiesAutoSelector/>
                {/* После EntitiesAutoSelector: тот снимает неподходящий выбор на смене этапа */}
                <UnitAutoSelector/>
                {gameType === GAME_TYPES.REPLAY ? (
                  <ReplayRunner
                    actions={replayGameplayActionsRef.current}
                    bootstrapActions={replayBootstrapActionsRef.current}
                    isRecordedTiming={isTutorialReplay}
                  />
                ) : (
                  <BotRunner
                    actionIntervalMs={tutorialChapterId
                      ? getTutorialBotActionIntervalMs(tutorialChapterId)
                      : gameType === GAME_TYPES.LOCAL ? hotSeatBotActionIntervalMs : BOT_ACTION_INTERVAL_MS}
                  />
                )}

                <Header
                  handleOpenInGameMenu={handleOpenInGameMenu}
                  isSpectatorMode={isSpectatorUIMode}
                />
                <InGameMenu
                  isOpen={isInGameMenuOpen}
                  onClose={handleCloseInGameMenu}
                  setScreen={setScreen}
                />
                {/* Боковые панели всегда смонтированы — в REPLAY и в
                локальном режиме зрителя видимость их собственных
                кнопок-табов переключается двойным кликом по CompletePhaseButton
                (isPanelsVisible из ReplayControlsContext), а не
                размонтированием компонента */}
                {tutorialChapterId && (
                  <>
                    <TutorialModalHost
                      onNextChapter={onTutorialNextChapter}
                      onOpenInventory={onTutorialOpenInventory}
                      onExitToMenu={onTutorialExitToMenu}
                      onBattle={onNewbieBattle}
                    />
                    <TutorialUiArrow/>
                  </>
                )}
                {/* Ярлыки рынка (только на телефоне) — колонкой от низа
                    шапки; список игроков открывает строка игроков в шапке */}
                <SideMenuTabsProvider>
                  <PlayersListSideMenu/>
                  <PolicyMarketSideMenu isSpectatorMode={isSpectatorUIMode}/>
                </SideMenuTabsProvider>
                {/* Автопереключение слоев поля; сама кнопка слоев — в левом
                    нижнем углу (Footer) */}
                <VisualTogglesAutoSwitch isSpectatorMode={isSpectatorUIMode}/>
                {/* Текущее задание обучения — в правом верхнем углу, где
                    раньше были слои */}
                {tutorialChapterId && <TutorialTasksPanel variant={'floating'}/>}

                {/* Сюжетная миссия: проигрыватель ленты (реплики, задания,
                    передача противника ИИ) и слой персонажа над футером */}
                <MissionRunner/>
                <MissionReplicaCharacter/>
                {missionId && (
                  <MissionCompleteModal
                    onRestart={onMissionRestart}
                    onOpenInventory={onMissionOpenInventory}
                    onExitToMenu={onMissionExitToMenu}
                  />
                )}
                {gameType === GAME_TYPES.REPLAY && <ReplayPlaybackPanel/>}
                {gameType === GAME_TYPES.REPLAY && isTutorialReplay && <TutorialReplayStatusPanel/>}

                <UnitUpgradeModal/>
                <InitiativeModal/>
                <RoundEndModal/>
                <CityCaptureModal/>
                <CombatResultsModal/>
                <GoblinsTrapKillNotice/>
                <RepairBuildingModal/>
                <ConstructionActionsConfirmModal/>
                <SupplyFarmsConfirmModal/>
                <SupplyRedistributeConfirmModal/>
                <WarStageUnitsConfirmModal/>
                <IsolationismModal/>
                <GameEndModal setScreen={setScreen} handleRankedGame={handleRankedGame}/>
                <MatchToastStack>
                  {/* Тосты видят все участники матча, включая зрителей и
                  смотрящих запись: они рассказывают о происходящем в партии, а
                  не только о собственных действиях. Прячутся лишь на быстрой
                  перемотке записи — см. GameToast */}
                  <GameToast/>
                  {/* Покупка политики — публичная информация о чужом ходе,
                  поэтому плашка видна всем участникам матча, включая зрителей
                  (как и ReactionToast) */}
                  <MatchPolicyToast/>
                  <ReactionToast/>
                </MatchToastStack>

                <Footer isSpectatorMode={isSpectatorUIMode}/>
              </> :
              <div className="loading-screen-overlay">
                <CampfireSparks/>

                {/* Представление игроков идет поверх загрузки поля — как и
                    задумано: сначала жребий, поле догружается за ним */}
                {isSetupRevealVisible ?
                  <SetupPlayersModal
                    onSkip={handleSkipSetupDelay}
                    isAwaitingPlayers={isForeignSetupState}
                    showVisibleBotMarks
                  />
                  : <LoadingScreen progress={loadingProgress}/>
                }
              </div>

            }
            {!hideGame ?
              (isPlayersReady &&
                <main className="main-container">
                  <div className="game-container">
                    <HexGrid
                      onLoadingProgress={handleLoadingProgress}
                      onGameReady={handleGameReady}
                      onRenderStalled={handleRenderStalled}
                    />
                  </div>
                </main>)
              : <div className="menu-screens-container">
                <h1 className="menu-title">Загрузка...</h1>
              </div>
            }
            {isRedrawing && (
              <div className="redraw-overlay">
                <h1 className="redraw-overlay-title">
                  {redrawReason === 'reconnect' ? 'Соединение восстановлено'
                    : redrawReason === 'stalled' ? 'Восстановление отрисовки'
                      : 'Размер окна изменился'}
                </h1>
                <h1 className="redraw-overlay-title">Перерисовываем поле...</h1>
              </div>
            )}
            </GameUiRootContext.Provider>
          </div>
        </ReplayControlsProvider>
      </PolicyMarketSideMenuProvider>
      </PlayersListSideMenuProvider>
    </SideMenuStackProvider>
  );
}

export default GameContent;